/**
 * Chat state management with Zustand
 */
import { create } from 'zustand';
import { sessionAPI, ChatStream } from '../services/api';
import { getChatErrorMessage } from '../utils/errorHandler';
import i18n from '../i18n';

const useChatStore = create((set, get) => ({
  // Sessions
  sessions: [],
  currentSession: null,
  loadingSessions: false,

  // Messages
  messages: [],
  isStreaming: false,
  streamingMessage: '',
  
  // Streaming status for UX enhancement
  streamingStatus: {
    phase: null,       // 'thinking' | 'tool_calling' | 'tool_executing' | 'responding' | null
    toolName: null,    // Current tool name being called/executed
    statusMessage: '', // Human-readable status message
  },

  // Chat stream instance
  chatStream: null,

  // ============================================
  // Session Actions
  // ============================================

  loadSessions: async () => {
    set({ loadingSessions: true });
    try {
      const data = await sessionAPI.list();
      set({ sessions: data.sessions, loadingSessions: false });
    } catch (error) {
      console.error('Error loading sessions:', error);
      set({ loadingSessions: false });
    }
  },

  createSession: async (title = null) => {
    try {
      const session = await sessionAPI.create(title);
      set((state) => ({
        sessions: [session, ...state.sessions],
        currentSession: session,
        messages: [],
      }));
      return session;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  },

  selectSession: async (sessionId) => {
    try {
      const data = await sessionAPI.get(sessionId);
      set({
        currentSession: data,
        messages: data.messages || [],
      });
    } catch (error) {
      console.error('Error loading session:', error);
    }
  },

  deleteSession: async (sessionId) => {
    try {
      await sessionAPI.delete(sessionId);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== sessionId),
        currentSession:
          state.currentSession?.id === sessionId ? null : state.currentSession,
        messages: state.currentSession?.id === sessionId ? [] : state.messages,
      }));
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  },

  // ============================================
  // Message Actions
  // ============================================

  sendMessage: async (text) => {
    const state = get();
    let sessionId = state.currentSession?.id;

    // Add user message to UI
    const userMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
    }));

    // Prepare assistant message ID (will be added when first chunk arrives)
    const assistantMessage = {
      id: `temp-assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
    };

    // Start streaming without adding empty message
    set({
      isStreaming: true,
      streamingMessage: '',
    });

    let assistantMessageAdded = false;

    // Create chat stream
    const stream = new ChatStream(
      (event) => {
        const state = get();

        switch (event.type) {
          case 'start':
            // Update session ID if new session was created
            if (event.session_id && !sessionId) {
              sessionId = event.session_id;
              set((state) => ({
                currentSession: { id: sessionId },
              }));
            }
            // Set initial thinking status
            set({
              streamingStatus: {
                phase: 'thinking',
                toolName: null,
                statusMessage: i18n.t('status.streaming.analyzing'),
              },
            });
            break;

          case 'thinking':
            // AI is thinking or calling tools
            const thinkingContent = event.content || '';
            const toolMatch = thinkingContent.match(/Executing tools?: (.+)/i);
            
            if (toolMatch) {
              // AI is calling tools
              const toolNames = toolMatch[1];
              set({
                streamingStatus: {
                  phase: 'tool_calling',
                  toolName: toolNames,
                  statusMessage: i18n.t('status.streaming.callingTool', { tool: toolNames }),
                },
              });
            } else {
              // Summarize long content (e.g., JSON data from tools)
              let statusMsg = thinkingContent;
              const MAX_STATUS_LENGTH = 80;
              
              // Check if content is JSON array (tool result data)
              if (thinkingContent.trim().startsWith('[')) {
                try {
                  const arr = JSON.parse(thinkingContent);
                  statusMsg = i18n.t('status.streaming.dataReceived', { count: arr.length });
                } catch {
                  statusMsg = i18n.t('status.streaming.processingData');
                }
              }
              // Check if content is JSON object
              else if (thinkingContent.trim().startsWith('{')) {
                statusMsg = i18n.t('status.streaming.processingData');
              }
              // Truncate long text
              else if (thinkingContent.length > MAX_STATUS_LENGTH) {
                statusMsg = thinkingContent.substring(0, MAX_STATUS_LENGTH) + '...';
              }
              
              set({
                streamingStatus: {
                  phase: 'thinking',
                  toolName: null,
                  statusMessage: statusMsg || i18n.t('status.streaming.thinking'),
                },
              });
            }
            break;

          case 'tool_result':
            // Tool execution completed
            const toolName = event.tool_name || 'tool';
            set({
              streamingStatus: {
                phase: 'tool_executing',
                toolName: toolName,
                statusMessage: i18n.t('status.streaming.toolResult', { tool: toolName }),
              },
            });
            break;

          case 'chunk':
          case 'response':
            // AI is responding with content
            const content = event.content || '';
            const isChunk = event.type === 'chunk';
            const isFinal = event.is_final === true;
            
            // Update status to responding
            set({
              streamingStatus: {
                phase: 'responding',
                toolName: null,
                statusMessage: i18n.t('status.streaming.generating'),
              },
            });
            
            if (isChunk) {
              // Append chunk to streaming message
              const newContent = state.streamingMessage + content;
              
              if (!assistantMessageAdded) {
                assistantMessageAdded = true;
                set((state) => ({
                  messages: [...state.messages, { ...assistantMessage, content: newContent }],
                  streamingMessage: newContent,
                }));
              } else {
                set({
                  streamingMessage: newContent,
                  messages: state.messages.map((msg) =>
                    msg.id === assistantMessage.id
                      ? { ...msg, content: newContent }
                      : msg
                  ),
                });
              }
            } else {
              // Full response (from host_agent pattern)
              if (!assistantMessageAdded) {
                assistantMessageAdded = true;
                set((state) => ({
                  messages: [...state.messages, { ...assistantMessage, content: content }],
                  streamingMessage: content,
                }));
              } else {
                set({
                  streamingMessage: content,
                  messages: state.messages.map((msg) =>
                    msg.id === assistantMessage.id
                      ? { ...msg, content: content }
                      : msg
                  ),
                });
              }
              
              // If final, complete streaming
              if (isFinal) {
                set({
                  isStreaming: false,
                  streamingMessage: '',
                  streamingStatus: {
                    phase: null,
                    toolName: null,
                    statusMessage: '',
                  },
                });
                
                get().loadSessions();
                if (sessionId) {
                  get().selectSession(sessionId);
                }
              }
            }
            break;

          case 'tool_call':
            // Legacy tool call event
            console.log('Tool call:', event.data);
            set({
              streamingStatus: {
                phase: 'tool_calling',
                toolName: event.data?.name || 'tool',
                statusMessage: i18n.t('status.streaming.callingTool', { tool: event.data?.name || 'tool' }),
              },
            });
            break;

          case 'tool_response':
            // Legacy tool response event
            console.log('Tool response:', event.data);
            break;

          case 'end':
            // Streaming complete
            set({
              isStreaming: false,
              streamingMessage: '',
              streamingStatus: {
                phase: null,
                toolName: null,
                statusMessage: '',
              },
            });

            // Reload sessions to update
            get().loadSessions();

            // If we have a session ID, reload it
            if (sessionId) {
              get().selectSession(sessionId);
            }
            break;

          case 'error':
            const errorMsg = i18n.t('errors.chat.streamError');
            console.error('Stream error:', errorMsg);
            // Add error message if assistant message not added yet
            if (!assistantMessageAdded) {
              set((state) => ({
                messages: [...state.messages, {
                  ...assistantMessage,
                  content: errorMsg,
                }],
                isStreaming: false,
                streamingMessage: '',
                streamingStatus: {
                  phase: null,
                  toolName: null,
                  statusMessage: '',
                },
              }));
            } else {
              set({
                isStreaming: false,
                streamingMessage: '',
                streamingStatus: {
                  phase: null,
                  toolName: null,
                  statusMessage: '',
                },
              });
            }
            break;

          default:
            break;
        }
      },
      (error) => {
        console.error('Stream connection error:', error);
        const errorMsg = i18n.t('errors.chat.streamError');
        // Add error message if not added yet, or update existing
        if (!assistantMessageAdded) {
          set((state) => ({
            messages: [...state.messages, {
              ...assistantMessage,
              content: errorMsg,
            }],
            isStreaming: false,
            streamingMessage: '',
          }));
        } else {
          set((state) => ({
            messages: state.messages.map((m) =>
              m.id === assistantMessage.id
                ? { ...m, content: errorMsg }
                : m
            ),
            isStreaming: false,
            streamingMessage: '',
          }));
        }
      }
    );

    set({ chatStream: stream });

    try {
      await stream.sendMessage(text, sessionId);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = i18n.t('errors.chat.sendError');
      // Add error message if not added yet, or update existing
      if (!assistantMessageAdded) {
        set((state) => ({
          messages: [...state.messages, {
            ...assistantMessage,
            content: errorMsg,
          }],
          isStreaming: false,
        }));
      } else {
        set((state) => ({
          messages: state.messages.map((m) =>
            m.id === assistantMessage.id
              ? { ...m, content: errorMsg }
              : m
          ),
          isStreaming: false,
        }));
      }
    }
  },

  clearMessages: () => {
    set({ messages: [], currentSession: null });
  },

  // ============================================
  // Cleanup
  // ============================================

  cleanup: () => {
    const state = get();
    if (state.chatStream) {
      state.chatStream.close();
    }
    set({
      sessions: [],
      currentSession: null,
      messages: [],
      isStreaming: false,
      streamingMessage: '',
      streamingStatus: {
        phase: null,
        toolName: null,
        statusMessage: '',
      },
      chatStream: null,
    });
  },
}));

export default useChatStore;

